<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  VARIABLE_SOURCES,
  normalizeColumnName,
} from 'dashboard/helper/campaignVariables';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';

// One variable of a template, shown inside the sentence it fills, with where its value comes from.
const props = defineProps({
  label: { type: String, required: true },
  caption: { type: String, default: '' },
  before: { type: String, default: '' },
  after: { type: String, default: '' },
  sourceOptions: { type: Array, required: true },
  // The columns of the spreadsheet already uploaded: once there is one, a column is picked from it
  // instead of named for the template spreadsheet.
  uploadedColumns: { type: Array, default: () => [] },
});

const source = defineModel('source', { type: String, default: '' });
const column = defineModel('column', { type: String, default: '' });
const text = defineModel('text', { type: String, default: '' });

const { t } = useI18n();

const comboBoxClass =
  '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6';

const columnOptions = computed(() =>
  props.uploadedColumns.map(name => ({ value: name, label: name }))
);

const columnName = computed(() => normalizeColumnName(column.value));

const isMissingFromUpload = computed(
  () =>
    !!columnName.value &&
    props.uploadedColumns.length > 0 &&
    !props.uploadedColumns.includes(columnName.value)
);

// ComboBox clears the selection when the selected option is clicked again; a variable always keeps
// a source once it has one.
const setSource = value => {
  if (value) source.value = value;
};

const setColumn = value => {
  if (value) column.value = value;
};
</script>

<template>
  <div class="flex flex-col gap-3 p-3 rounded-lg bg-n-alpha-black2">
    <div class="flex flex-col gap-1">
      <span v-if="caption" class="text-xs font-medium text-n-slate-11">
        {{ caption }}
      </span>
      <p class="mb-0 text-sm leading-relaxed text-n-slate-11">
        {{ before
        }}<span
          class="px-1 py-0.5 font-medium rounded bg-n-blue-3 text-n-blue-11"
          >{{ label }}</span
        >{{ after }}
      </p>
    </div>

    <div class="grid gap-2 sm:grid-cols-2">
      <ComboBox
        :model-value="source"
        :options="sourceOptions"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.SOURCE_PLACEHOLDER')
        "
        :class="comboBoxClass"
        @update:model-value="setSource"
      />
      <template v-if="source === VARIABLE_SOURCES.COLUMN">
        <ComboBox
          v-if="uploadedColumns.length"
          :model-value="columnName"
          :options="columnOptions"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.COLUMN_SELECT_PLACEHOLDER')
          "
          :class="comboBoxClass"
          @update:model-value="setColumn"
        />
        <Input
          v-else
          v-model="column"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.COLUMN_INPUT_PLACEHOLDER')
          "
        />
      </template>
      <Input
        v-else-if="source === VARIABLE_SOURCES.TEXT"
        v-model="text"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.TEXT_PLACEHOLDER')"
      />
    </div>

    <p v-if="isMissingFromUpload" class="mb-0 text-xs text-n-amber-11">
      {{
        t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.COLUMN_NOT_IN_UPLOAD', {
          column: columnName,
        })
      }}
    </p>
    <p
      v-else-if="
        source === VARIABLE_SOURCES.COLUMN &&
        columnName &&
        !uploadedColumns.length
      "
      class="mb-0 text-xs text-n-slate-11"
    >
      {{
        t('CAMPAIGN.WHATSAPP.CREATE.VARIABLES.COLUMN_HINT', {
          column: columnName,
        })
      }}
    </p>
  </div>
</template>
